"use client";

import { SafeHydrate } from "@/SafeHydrate";
import { useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const testimonials = [{
  id: 1,
  name: "Sarah Mitchell",
  role: "Marketing Director",
  company: "TechCorp",
  quote: "MEDIA1PRO delivered absolutely stunning visuals for our brand campaign. Their cinematic approach elevated our content beyond expectations.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1529178/img/client_sarah_mitchell.png"
}, {
  id: 2,
  name: "David Chen",
  role: "Event Coordinator",
  company: "EventsPro",
  quote: "Professional, punctual, and incredibly talented. The event coverage was flawless and captured every important moment beautifully.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1529178/img/client_david_chen.png"
}, {
  id: 3,
  name: "Emily Rodriguez",
  role: "CEO",
  company: "Luxe Brands",
  quote: "The quality of their work is unmatched. Our product shoots look premium and have significantly increased our conversion rates.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1529178/img/client_emily_rodriguez.png"
}];
export default function ClientTestimonials() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.2,
    once: true
  });
  const handlePrevious = () => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setTimeout(() => {
      setCurrentIndex(prev => prev === 0 ? testimonials.length - 1 : prev - 1);
      setIsTransitioning(false);
    }, 250);
  };
  const handleNext = () => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setTimeout(() => {
      setCurrentIndex(prev => prev === testimonials.length - 1 ? 0 : prev + 1);
      setIsTransitioning(false);
    }, 250);
  };
  const current = testimonials[currentIndex];
  return <section id="client-testimonials" ref={ref} className={`py-20 bg-light-background transition-opacity duration-[var(--duration-medium)] ${isVisible ? "opacity-100" : "opacity-0"}`}>
      <div className="container mx-auto px-4 max-w-5xl">
        <h2 className="text-4xl font-display font-bold text-center mb-4 text-foreground-light">
          Client Testimonials
        </h2>
        <p className="text-center text-muted-foreground mb-16 max-w-2xl mx-auto">
          Hear from brands and teams who trusted us to create premium content that delivers results.
        </p>

        <div className="relative group">
          <div className="overflow-hidden">
            <div key={current.id} data-index={currentIndex} className={`flex flex-col md:flex-row items-center gap-8 md:gap-12 transition-all duration-[250ms] ease-smooth ${isTransitioning ? "opacity-0 translate-x-4" : "opacity-100 translate-x-0"}`}>
              <div className="flex-shrink-0">
                <SafeHydrate><Avatar className="w-32 h-32 md:w-40 md:h-40 border-4 border-accent shadow-medium">
                  <AvatarImage src={current.image} alt={current.name} />
                  <AvatarFallback className="bg-primary text-foreground-primary text-3xl font-semibold">
                    {current.name.split(" ").map(n => n[0]).join("")}
                  </AvatarFallback>
                </Avatar></SafeHydrate>
              </div>

              <div className="flex-1 text-center md:text-left">
                <blockquote className="text-lg md:text-xl text-foreground-light leading-relaxed mb-6 font-sans">
                  "{current.quote}"
                </blockquote>
                <div>
                  <p className="font-display font-semibold text-foreground-light text-lg">
                    {current.name}
                  </p>
                  <p className="text-muted-foreground text-sm">
                    {current.role} at {current.company}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="absolute top-1/2 -translate-y-1/2 left-0 right-0 flex justify-between pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-[var(--duration-medium)]">
            <Button variant="outline" size="icon" onClick={handlePrevious} className="pointer-events-auto -translate-x-4 md:-translate-x-12 bg-light-background/90 hover:bg-light-background border-border shadow-medium hover:shadow-strong hover:-translate-y-0.5 transition-all duration-[var(--duration-medium)]" aria-label="Previous testimonial">
              <ChevronLeft className="w-5 h-5" />
            </Button>
            <Button variant="outline" size="icon" onClick={handleNext} className="pointer-events-auto translate-x-4 md:translate-x-12 bg-light-background/90 hover:bg-light-background border-border shadow-medium hover:shadow-strong hover:-translate-y-0.5 transition-all duration-[var(--duration-medium)]" aria-label="Next testimonial">
              <ChevronRight className="w-5 h-5" />
            </Button>
          </div>

          <div className="flex justify-center gap-2 mt-12">
            {testimonials.map((_, index) => <Button key={index} data-index={index} variant="ghost" size="icon" onClick={() => {
            if (!isTransitioning) {
              setIsTransitioning(true);
              setTimeout(() => {
                setCurrentIndex(index);
                setIsTransitioning(false);
              }, 250);
            }
          }} className={`w-8 h-8 p-0 transition-all duration-[var(--duration-medium)] hover:bg-transparent ${index === currentIndex ? "bg-transparent" : "bg-transparent"}`} aria-label={`Go to testimonial ${index + 1}`}>
                <span className={`block rounded-full transition-all duration-[var(--duration-medium)] ${index === currentIndex ? "bg-accent w-8 h-2" : "bg-border w-2 h-2 hover:bg-accent/50"}`} />
              </Button>)}
          </div>
        </div>
      </div>
    </section>;
}